import { KeyboardEvent, useMemo, useState } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { Button, Input, InputNumber, Table } from 'antd';

import {
  ConnectorPower,
  FixedPowerRow,
  MIN_CURRENT_A,
  applyToAll,
  chargerMaxText,
  evenSplit,
  fixedPowerMax,
  fixedPowerSums,
  powerOf,
  setPower,
} from './fixedPower';
import * as S from './styled';

export type FixedPowerTableProps = {
  rows: FixedPowerRow[];
  powers: ConnectorPower[] | null | undefined;
  algorithm: string | null | undefined;
  nominalA: number;
  disabled?: boolean;
  onChange: (powers: ConnectorPower[]) => void;
};

const onlyDigits = (value?: string | number) =>
  String(value ?? '').replace(/\D/g, '');

// Teclas de edição/navegação continuam valendo; qualquer outro caractere que não seja dígito é ignorado
const blockNonDigit = (event: KeyboardEvent<HTMLInputElement>) => {
  if (
    event.key.length === 1 &&
    !/\d/.test(event.key) &&
    !event.ctrlKey &&
    !event.metaKey
  ) {
    event.preventDefault();
  }
};

/** A partir daqui a tabela rola por dentro, com o cabeçalho fixo */
const SCROLL_FROM_ROWS = 8;

/** Potência fixa de cada conector do grupo no modo estático (MV2-2005) */
const FixedPowerTable = ({
  rows,
  powers,
  algorithm,
  nominalA,
  disabled = false,
  onChange,
}: FixedPowerTableProps) => {
  const [search, setSearch] = useState('');
  const [bulkValue, setBulkValue] = useState<number | null>(null);
  const sums = nominalA
    ? fixedPowerSums(algorithm, nominalA, rows, powers)
    : [];

  // Conectores da mesma estação juntos, e a estação aparece uma vez só (rowSpan)
  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows
      .filter((row) => !term || row.station.toLowerCase().includes(term))
      .sort((a, b) => a.station.localeCompare(b.station) || a.seq - b.seq);
  }, [rows, search]);
  // A estação aparece uma vez, na primeira linha dos seus conectores
  const stationSpan = useMemo(() => {
    const spans = new Map<string, number>();
    visibleRows.forEach((row, index) => {
      const first =
        index === 0 || visibleRows[index - 1].station !== row.station;
      spans.set(
        row.connector_id,
        first ? visibleRows.filter((r) => r.station === row.station).length : 0,
      );
    });
    return spans;
  }, [visibleRows]);
  const stations = new Set(rows.map((row) => row.station)).size;
  const filtered = visibleRows.length !== rows.length;

  return (
    <S.FixedPower data-testid='smart-charging-fixed-power'>
      <S.Capacity data-testid='smart-charging-fixed-power-sum'>
        {sums.map(({ label, sum, limit }) => {
          const over = sum > limit;
          return (
            <S.CapacityRow key={label}>
              <S.CapacityHeader>
                <span>{label === 'Total' ? 'Distribuído' : label}</span>
                <strong>{`${sum} A de ${limit} A`}</strong>
              </S.CapacityHeader>
              <S.CapacityTrack>
                <S.CapacityFill
                  $percent={
                    limit > 0 ? Math.min(100, (sum / limit) * 100) : 100
                  }
                  $over={over}
                />
              </S.CapacityTrack>
              <S.CapacityNote $over={over}>
                {over
                  ? `Passou ${sum - limit} A do limite`
                  : `Sobram ${limit - sum} A`}
              </S.CapacityNote>
            </S.CapacityRow>
          );
        })}
      </S.Capacity>

      <S.Toolbar>
        <Input
          data-testid='fixed-power-search'
          allowClear
          prefix={<SearchOutlined />}
          placeholder='Buscar estação'
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ maxWidth: 260 }}
        />
        <S.ToolbarActions>
          <span>{filtered ? 'Aplicar aos filtrados' : 'Aplicar a todos'}</span>
          <InputNumber
            data-testid='fixed-power-bulk'
            min={MIN_CURRENT_A}
            step={1}
            precision={0}
            formatter={onlyDigits}
            parser={onlyDigits}
            onKeyDown={blockNonDigit}
            disabled={disabled}
            value={bulkValue ?? undefined}
            onChange={(value: any) =>
              setBulkValue(
                value === null || value === undefined || value === ''
                  ? null
                  : Number(value),
              )
            }
            style={{ width: 72 }}
          />
          <Button
            data-testid='fixed-power-bulk-apply'
            disabled={disabled || !bulkValue || !visibleRows.length}
            onClick={() =>
              bulkValue && onChange(applyToAll(powers, visibleRows, bulkValue))
            }
          >
            Aplicar
          </Button>
          <Button
            data-testid='fixed-power-split'
            disabled={disabled || !nominalA || !rows.length}
            onClick={() => onChange(evenSplit(algorithm, nominalA, rows))}
          >
            Dividir igualmente
          </Button>
        </S.ToolbarActions>
      </S.Toolbar>
      <S.TableMeta>
        {filtered
          ? `${visibleRows.length} de ${rows.length} conectores`
          : `${rows.length} ${
              rows.length === 1 ? 'conector' : 'conectores'
            } em ${stations} ${stations === 1 ? 'estação' : 'estações'}`}
      </S.TableMeta>

      <Table<FixedPowerRow>
        size='small'
        pagination={false}
        rowKey='connector_id'
        dataSource={visibleRows}
        scroll={visibleRows.length > SCROLL_FROM_ROWS ? { y: 420 } : undefined}
        locale={{
          emptyText: filtered
            ? 'Nenhuma estação encontrada'
            : 'Nenhum conector nas estações do grupo',
        }}
        columns={[
          {
            title: 'Estação',
            dataIndex: 'station',
            // antd 4.10: rowSpan pelo render (o onCell não recebe a linha agrupada)
            render: (station: string, row: FixedPowerRow) => ({
              children: <S.StationName>{station}</S.StationName>,
              props: { rowSpan: stationSpan.get(row.connector_id) ?? 1 },
            }),
          },
          { title: 'Conector', dataIndex: 'seq', width: 96 },
          {
            title: 'Fase',
            dataIndex: 'phase',
            width: 96,
            render: (phase: string | null) => phase || 'A, B e C',
          },
          {
            title: 'Máx. do carregador',
            key: 'charger-max',
            width: 160,
            render: (_: any, row: FixedPowerRow) => (
              <span data-testid={`charger-max-${row.connector_id}`}>
                {chargerMaxText(row)}
              </span>
            ),
          },
          {
            title: 'Potência fixa (A)',
            key: 'power',
            width: 220,
            render: (_: any, row: FixedPowerRow) => {
              const max = fixedPowerMax(algorithm, nominalA, rows, powers, row);
              return (
                <S.PowerCell>
                  {/* Campo de texto controlado: o InputNumber do antd 4.10 corrige sozinho o valor
                      acima do max (troca valores gravados ao abrir e espreme o "Aplicar a todos") e só
                      mostra o limite ao sair do campo */}
                  <Input
                    data-testid={`fixed-power-${row.connector_id}`}
                    inputMode='numeric'
                    style={{ width: 80 }}
                    disabled={disabled}
                    onKeyDown={blockNonDigit}
                    value={powerOf(powers, row.connector_id) ?? ''}
                    onChange={(event) => {
                      const digits = onlyDigits(event.target.value);
                      onChange(
                        setPower(
                          powers,
                          row.connector_id,
                          digits === ''
                            ? null
                            : // Limita já na digitação; abaixo do mínimo fica para a validação ao salvar
                              Math.min(Number(digits), max ?? Number(digits)),
                        ),
                      );
                    }}
                  />
                  {max !== undefined && (
                    <S.PowerHint
                      data-testid={`fixed-power-max-${row.connector_id}`}
                      // Acima do que cabe (ex.: valores gravados ou aplicados a todos): a dica pede para baixar
                      $over={(powerOf(powers, row.connector_id) ?? 0) > max}
                    >
                      até {max} A
                    </S.PowerHint>
                  )}
                </S.PowerCell>
              );
            },
          },
        ]}
      />
    </S.FixedPower>
  );
};

export default FixedPowerTable;
